import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import emailjs from '@emailjs/browser';
import { FaUser, FaEnvelope, FaPhone, FaComment, FaTimes, FaList } from 'react-icons/fa';
import TopHeader from './components/TopHeader';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import Testimonials from './pages/Testimonials';
import Products from './pages/Products';
import Career from './pages/Career';
import Branches from './pages/Branches';
import ContactUs from './pages/ContactUs';

function App() {
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    product: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formRef = useRef(null);
  const modalRef = useRef(null);
  const popupRef = useRef(null);

  const products = [
    'Logbook Loans',
    'Motor Insurance',
    'Unsecured Bid Bonds',
    'Asset Financing',
    'Medical Insurance',
    'Performance Bonds',
  ];

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const openApplyModal = () => setIsApplyModalOpen(true);
  const closeApplyModal = () => {
    setIsApplyModalOpen(false);
    setFormData({ fullName: '', email: '', phone: '', product: '', message: '' });
    setErrors({});
    setShowSuccessPopup(false);
    setSubmitError('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?\d{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone number is invalid (10-15 digits)';
    }
    if (!formData.product) newErrors.product = 'Product is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        console.warn('EmailJS credentials missing. Simulating submission.');
        await new Promise((resolve) => setTimeout(resolve, 1000));
        console.log('Apply Form data (simulated):', formData);
      } else {
        const response = await emailjs.send(
          serviceId,
          templateId,
          {
            from_name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            product: formData.product,
            message: formData.message,
          },
          publicKey
        );
        console.log('Email sent successfully:', response);
      }
      setFormData({ fullName: '', email: '', phone: '', product: '', message: '' });
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
        closeApplyModal();
      }, 5000);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Failed to send application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isApplyModalOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        if (e.key === 'Escape') {
          closeApplyModal();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      if (firstElement) firstElement.focus();

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isApplyModalOpen]);

  const handleModalClick = (e) => {
    e.stopPropagation(); // Prevent clicks inside modal from triggering overlay's onClick
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-inter">
      {/* <TopHeader /> */}
      <Navbar openApplyModal={openApplyModal} />
      <main className="flex-grow pt-[48px]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/products" element={<Products />} />
          <Route path="/careers" element={<Career />} />
          <Route path="/branches" element={<Branches />} />
          <Route path="/contact-us" element={<ContactUs />} />
        </Routes>
      </main>
      <Footer />
      {/* Apply Today Modal */}
      {isApplyModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-end z-50 animate-fade-in"
          role="dialog"
          aria-label="Apply Today Form"
          aria-modal="true"
          onClick={closeApplyModal}
        >
          <div
            ref={modalRef}
            className={`bg-white rounded-l-lg p-6 sm:p-8 w-full max-w-md h-full mr-0 shadow-xl transform ${
              isApplyModalOpen ? 'animate-slide-in-right' : 'animate-slide-out'
            }`}
            onClick={handleModalClick}
          >
            <div className="text-center mb-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-inter font-bold text-[--maasai-blue] tracking-tight flex-1 text-center">
                  Apply Today
                </h2>
                <button
                  onClick={closeApplyModal}
                  className="text-[--maasai-blue] hover:text-[--maasai-red] focus:ring-2 focus:ring-[--maasai-red] focus:ring-offset-2 rounded-full"
                  aria-label="Close modal"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
              <hr className="border-t border-[--maasai-blue] my-4 w-full max-w-xs mx-auto" />
            </div>
            <p className="text-sm font-inter font-medium text-gray-600 mb-6 text-center">
              One of our representatives will get back to you in less than 2 hours.
            </p>
            {submitError && (
              <div
                className="bg-red-100 text-red-800 p-4 rounded-lg mb-6 animate-fade-in"
                role="alert"
                aria-live="polite"
              >
                {submitError}
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-inter font-semibold text-[--maasai-blue] mb-2"
                  >
                    Full Name
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 text-sm border ${
                        errors.fullName ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/50'
                      } rounded-lg shadow-sm focus:outline-none focus:border-[--maasai-blue] focus:ring-2 focus:ring-[--maasai-blue]/50 transition-all duration-300`}
                      placeholder="Enter your full name"
                      aria-describedby={errors.fullName ? 'fullName-error' : undefined}
                      required
                    />
                  </div>
                  {errors.fullName && (
                    <p id="fullName-error" className="text-[--maasai-red] text-sm mt-1" role="alert">
                      {errors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-inter font-semibold text-[--maasai-blue] mb-2"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 text-sm border ${
                        errors.email ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/50'
                      } rounded-lg shadow-sm focus:outline-none focus:border-[--maasai-blue] focus:ring-2 focus:ring-[--maasai-blue]/50 transition-all duration-300`}
                      placeholder="Enter your email"
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      required
                    />
                  </div>
                  {errors.email && (
                    <p id="email-error" className="text-[--maasai-red] text-sm mt-1" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-inter font-semibold text-[--maasai-blue] mb-2"
                  >
                    Phone
                  </label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 text-sm border ${
                        errors.phone ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/50'
                      } rounded-lg shadow-sm focus:outline-none focus:border-[--maasai-blue] focus:ring-2 focus:ring-[--maasai-blue]/50 transition-all duration-300`}
                      placeholder="Enter your phone number"
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                      required
                    />
                  </div>
                  {errors.phone && (
                    <p id="phone-error" className="text-[--maasai-red] text-sm mt-1" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="product"
                    className="block text-sm font-inter font-semibold text-[--maasai-blue] mb-2"
                  >
                    Product
                  </label>
                  <div className="relative">
                    <FaList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      id="product"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 text-sm border ${
                        errors.product ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/50'
                      } rounded-lg shadow-sm focus:outline-none focus:border-[--maasai-blue] focus:ring-2 focus:ring-[--maasai-blue]/50 transition-all duration-300`}
                      aria-describedby={errors.product ? 'product-error' : undefined}
                      required
                    >
                      <option value="" disabled>
                        Select a product
                      </option>
                      {products.map((product, index) => (
                        <option key={index} value={product}>
                          {product}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.product && (
                    <p id="product-error" className="text-[--maasai-red] text-sm mt-1" role="alert">
                      {errors.product}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-inter font-semibold text-[--maasai-blue] mb-2"
                >
                  Message
                </label>
                <div className="relative">
                  <FaComment className="absolute left-3 top-4 text-gray-400" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 text-sm border ${
                      errors.message ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/50'
                    } rounded-lg shadow-sm focus:outline-none focus:border-[--maasai-blue] focus:ring-2 focus:ring-[--maasai-blue]/50 transition-all duration-300 resize-y`}
                    rows="4"
                    placeholder="Tell us about your needs"
                    aria-describedby={errors.message ? 'message-error' : undefined}
                    required
                  ></textarea>
                </div>
                {errors.message && (
                  <p id="message-error" className="text-[--maasai-red] text-sm mt-1" role="alert">
                    {errors.message}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={closeApplyModal}
                  className="bg-white border border-[--maasai-blue]/50 text-[--maasai-blue] font-inter font-medium px-6 py-2 rounded-lg hover:bg-gray-100 hover:text-[--maasai-red] transition-all duration-300 text-sm focus:ring-2 focus:ring-[--maasai-red] focus:ring-offset-2"
                  aria-label="Cancel application"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-[--maasai-blue] to-[--maasai-red] text-white font-inter font-medium px-6 py-2 rounded-lg hover:scale-105 hover:shadow-lg transition-all duration-300 text-sm focus:ring-2 focus:ring-[--maasai-blue] focus:ring-offset-2 flex items-center gap-2"
                  aria-label="Submit application"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
              {showSuccessPopup && (
                <div
                  ref={popupRef}
                  className="mt-6 bg-white border border-[--maasai-blue]/20 rounded-lg p-6 shadow-lg animate-fade-in"
                  role="alert"
                  aria-live="polite"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-inter font-semibold text-[--maasai-blue]">
                      Application Sent Successfully!
                    </h3>
                    <button
                      onClick={() => {
                        setShowSuccessPopup(false);
                        closeApplyModal();
                      }}
                      className="text-gray-600 hover:text-[--maasai-red] focus:ring-2 focus:ring-[--maasai-red] focus:ring-offset-2 rounded-full"
                      aria-label="Close success popup"
                    >
                      <FaTimes className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm font-inter text-gray-600">
                    Thank you for applying with TGL Credit. We’ll respond within 2 hours.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      <style>
        {`
          :root {
            --maasai-blue: #2F498C;
            --maasai-red: #843345;
          }
          .glassmorphism {
            background: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.6s ease-out forwards;
          }
          @keyframes slide-in-right {
            from {
              transform: translateX(100%) scale(0.95);
              opacity: 0;
            }
            to {
              transform: translateX(0) scale(1);
              opacity: 1;
            }
          }
          .animate-slide-in-right {
            animation: slide-in-right 0.6s ease-out forwards;
          }
          @keyframes slide-out {
            from {
              transform: translateX(0) scale(1);
              opacity: 1;
            }
            to {
              transform: translateX(100%) scale(0.95);
              opacity: 0;
            }
          }
          .animate-slide-out {
            animation: slide-out 0.6s ease-out forwards;
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-in, .animate-slide-in-right, .animate-slide-out, .focus\\:scale-105, .hover\\:scale-105 {
              animation: none;
              transform: none;
              opacity: 1;
              transition: none;
            }
          }
        `}
      </style>
    </div>
  );
}

export default App;